import React, { useState, useMemo } from "react";
import { Offcanvas } from "react-bootstrap";
import {
  FaStar,
  FaExclamationTriangle,
  FaCrown,
  FaFolderPlus,
} from "react-icons/fa";
import { ArrowUpRight, Target, TrendingUp } from "react-feather";
import { Link } from "react-router-dom";

const getSegmentMeta = (segment) => {
  switch (segment) {
    case "loyal_customers":
      return {
        title: "Loyal Customers",
        color: "#e7f5ff",
        icon: <FaStar />,
        iconBg: "#339af0",
        action: "Reward loyalty",
        impact: "Increase lifetime value",
      };
    case "at_risk_customers":
      return {
        title: "At Risk",
        color: "#fff5f5",
        icon: <FaExclamationTriangle />,
        iconBg: "#fa5252",
        action: "Send win-back offers",
        impact: "Recover lost revenue",
      };
    case "new_customers":
      return {
        title: "New Customers",
        color: "#e6fcf5",
        icon: <FaFolderPlus />,
        iconBg: "#00cb93",
        action: "Welcome & engage",
        impact: "Convert to repeat buyers",
      };
    case "high_value_customers":
      return {
        title: "High Value",
        color: "#f3f0ff",
        icon: <FaCrown />,
        iconBg: "#7a59ff",
        action: "Prioritize & upsell",
        impact: "Maximize revenue",
      };
    default:
      return {
        title: segment,
        color: "#f8f9fa",
        icon: <FaStar />,
        iconBg: "#adb5bd",
        action: "",
        impact: "",
      };
  }
};

const CustomerSegmentsGrid = ({ segments = [] }) => {
  const [panel, setPanel] = useState({ show: false, segment: null });
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("days_to_reorder");

  const handleOpen = (segment) => {
    setPanel({ show: true, segment });
    setSearch("");
    setSortBy("days_to_reorder");
  };

  const handleClose = () => {
    setPanel({ show: false, segment: null });
  };

  const currentSegment = segments.find(
    (s) => s.segment === panel.segment
  );

  const customers = currentSegment?.customers || [];
  const meta = getSegmentMeta(panel.segment);

  // 🔍 FILTER + SORT
  const processedCustomers = useMemo(() => {
    return customers
      .filter((c) =>
        c.name?.toLowerCase().includes(search.toLowerCase())
      )
      .sort((a, b) => {
        const getReorder = (c) =>
          c.days_to_reorder?.parsedValue ?? c.days_to_reorder ?? 0;

        if (sortBy === "orders") {
          return (b.orders || 0) - (a.orders || 0);
        }

        return getReorder(a) - getReorder(b);
      });
  }, [customers, search, sortBy]);

  return (
    <>
      {/* SEGMENT GRID */}
      <div className="row g-3 mb-2">
        <h5 className="font-bold">Customer Segments</h5>

        {segments.map((seg, i) => {
          const meta = getSegmentMeta(seg.segment);

          const totalValue = seg.customers.reduce(
            (sum, c) => sum + (c.orders || 0),
            0
          );

          return (
            <div className="col-6 mt-1 mb-3" key={i}>
              <div
                className="p-3 rounded-4 shadow-tile h-100"
                style={{ background: meta.color, cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpen(seg.segment);
                }}
              >
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "10px",
                      background: meta.iconBg,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {meta.icon}
                  </div>
                  <div className="fw-semibold">{meta.title}</div>
                </div>

                <div className="fw-bold">
                  {totalValue.toLocaleString()}{" "}
                  <span className="small">Orders</span>
                </div>

                <div className="text-muted small mt-2">
                  View customers →
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* SIDEBAR */}
      <Offcanvas
        show={panel.show}
        onHide={handleClose}
        placement="end"
        className="bg-lblue0 mini-sidebar-cust"
      >
        <Offcanvas.Header closeButton className="bg-lblue0 p-3 pt-2 pb-2">
          <h5 className="fw-semibold m-0">Customer Segments</h5>
        </Offcanvas.Header>

        <Offcanvas.Body>

          {/* 🧠 SEGMENT INSIGHT */}
          <div className="mb-3 p-3 rounded-4 bg-white">
            <h5 className="fw-semibold m-0">{meta?.title}</h5>
            <div className="font9 text-muted mb-2 mt-2">Recommended Action</div>

            {/* ACTION */}
            <div className="d-flex align-items-start gap-2 mb-2 font9">
                <div
                className="d-flex align-items-center justify-content-center"
                style={{
                    width: "26px",
                    height: "26px",
                    borderRadius: "6px",
                    background: "#eef2ff",
                    color: "#4c6ef5",
                    fontSize: "14px",
                }}
                >
                <Target />
                </div>

                <div>
                <div className="fw-semibold">{meta.action}</div>
                </div>
            </div>

            {/* IMPACT */}
            <div className="d-flex align-items-start gap-2">
                <div
                className="d-flex align-items-center justify-content-center"
                style={{
                    width: "26px",
                    height: "26px",
                    borderRadius: "6px",
                    background: "#e6fcf5",
                    color: "#00a67e",
                    fontSize: "14px",
                }}
                >
                <TrendingUp />
                </div>

                <div>
                <div className="text-muted small">{meta.impact}</div>
                </div>
            </div>

        </div>

          {/* 🔍 SEARCH + SORT */}
          <div className="d-flex gap-2 mb-3">
            <input
              type="text"
              className="form-control form-control-sm bg-white rounded-3 shadow-tile border-0"
              placeholder="Search customer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select
              className="form-select form-select-sm w-auto bg-white rounded-3 shadow-tile border-0"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="days_to_reorder">Urgency</option>
              <option value="orders">Orders</option>
            </select>
          </div>

          {/* CUSTOMER LIST */}
          {processedCustomers.length === 0 ? (
            <div className="text-center p-4 bg-inherit rounded-4">

              <p className="mb-1 fw-semibold">
                This segment doesn’t have customers yet
              </p>

              <small className="text-muted">
                As customers start falling into this group, they’ll appear here with recommended actions.
              </small>

            </div>
          ) : (
            processedCustomers.map((c, index) => {
              const reorder =
                c.days_to_reorder?.parsedValue ??
                c.days_to_reorder;

              const isOverdue = reorder < 0;

              return (
                <Link
                  key={index}
                  to={c.link || "#"}
                  className="text-decoration-none text-reset"
                >
                  <div
                    className="d-flex justify-content-between align-items-center p-3 mb-2 rounded-4 border-dlblue"
                    style={{ background: "#fff" }}
                  >
                    {/* LEFT */}
                    <div className="d-flex align-items-center gap-2">

                      <div
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "8px",
                          background:
                            index === 0 ? "#f5f1dc" : "#eef1f4",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        {index + 1}
                      </div>

                      <div
                        style={{
                          width: "30px",
                          height: "30px",
                          borderRadius: "50%",
                          background: "#ddd",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        {c.name?.charAt(0)}
                      </div>

                      <div>
                        <div className="font8 fw-semibold">{c.name}</div>
                        <div className="text-muted font8">
                          {c.last_order_days}d ago • {c.orders} orders
                        </div>
                      </div>
                    </div>

                    {/* RIGHT */}
                    <div className="text-end">

                      {reorder !== null && (
                        <div
                          className={`font8 ${
                            isOverdue ? "text-danger" : "text-warning"
                          }`}
                        >
                          {isOverdue
                            ? `${Math.round(Math.abs(reorder))}d overdue`
                            : `${reorder}d`}
                        </div>
                      )}

                      <ArrowUpRight size={14} />
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default CustomerSegmentsGrid;